import React, { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { Reveal } from "./useReveal";
import { CONTACT } from "./data";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Field sets per lead type. Every form posts to the site backend
// (POST /api/leads). If the request fails for any reason, we fall back to a
// pre-filled email so no lead is ever lost.
const VARIANTS = {
  buyer: {
    heading: "Buyer Inquiry",
    button: "Request Buyer Help",
    fields: [
      { name: "area", label: "Area of Interest", placeholder: "e.g. Long Beach, Cerritos, Orange County" },
      { name: "price", label: "Price Range", placeholder: "e.g. $600k – $900k" },
    ],
  },
  seller: {
    heading: "Home Valuation Request",
    button: "Request My Home Value",
    fields: [
      { name: "address", label: "Property Address", placeholder: "Street, City, ZIP", required: true },
      { name: "timeline", label: "Timeline", placeholder: "e.g. 0–3 months, exploring options" },
    ],
  },
  financing: {
    heading: "Financing Inquiry",
    button: "Get Financing Guidance",
    fields: [
      { name: "goal", label: "What are you looking to do?", placeholder: "e.g. buy a first home, refinance, investment" },
    ],
  },
  contact: {
    heading: "Contact Form",
    button: "Send Message",
    fields: [],
  },
  agent: {
    heading: "Agent Application",
    button: "Apply to Join",
    fields: [
      { name: "license", label: "DRE License #", placeholder: "e.g. 01234567", required: true },
      { name: "experience", label: "Years of Experience", placeholder: "e.g. 2 years, 15 deals closed" },
      { name: "brokerage", label: "Current Brokerage", placeholder: "e.g. independent / XYZ Realty" },
    ],
  },
  call: {
    heading: "Book a Call",
    button: "Request a Call Back",
    fields: [
      { name: "besttime", label: "Best Time to Call", placeholder: "e.g. weekday mornings" },
    ],
  },
};

export const LeadForm = ({ variant = "contact", testid }) => {
  const cfg = VARIANTS[variant] || VARIANTS.contact;
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [form, setForm] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const payload = { type: variant, ...form, page: window.location.pathname };
    try {
      const res = await fetch(`${BACKEND}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("done");
    } catch (err) {
      // Fallback: open a pre-filled email so the lead still reaches us.
      const body = Object.entries(payload)
        .map(([k, v]) => `${k}: ${v}`)
        .join("%0D%0A");
      window.location.href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(
        `Website ${cfg.heading} — ${form.name || ""}`
      )}&body=${body}`;
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div
        data-testid={`${testid || variant}-success`}
        className="bg-white border border-[#E5E5E5] rounded-[20px] p-10 text-center"
      >
        <CheckCircle2 className="mx-auto text-[#C9A961]" size={44} />
        <p className="mt-4 font-heading font-bold text-xl text-[#2D2D37]">Thank you!</p>
        <p className="mt-2 text-sm text-[#2D2D37]/60 max-w-sm mx-auto">
          Your message is on its way. A member of the Wallstreet Realty team
          will reach out shortly. Need us sooner? Call {CONTACT.phone}.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full bg-white border border-[#E5E5E5] rounded-xl px-4 py-3 text-[15px] text-[#2D2D37] outline-none focus:border-[#C9A961] transition-colors";

  return (
    <Reveal>
      <form
        onSubmit={submit}
        data-testid={testid || `${variant}-lead-form`}
        className="bg-white border border-[#E5E5E5] rounded-[20px] p-6 sm:p-8 text-left shadow-sm"
      >
        <p className="font-heading font-bold text-xl text-[#2D2D37]">{cfg.heading}</p>
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <label className="block sm:col-span-1">
            <span className="text-xs uppercase tracking-[0.12em] text-[#2D2D37]/50 font-medium">Full Name *</span>
            <input required autoComplete="name" className={`mt-1.5 ${inputCls}`} placeholder="Your name" onChange={set("name")} />
          </label>
          <label className="block sm:col-span-1">
            <span className="text-xs uppercase tracking-[0.12em] text-[#2D2D37]/50 font-medium">Phone *</span>
            <input required type="tel" autoComplete="tel" className={`mt-1.5 ${inputCls}`} placeholder="(555) 555-5555" onChange={set("phone")} />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.12em] text-[#2D2D37]/50 font-medium">Email *</span>
            <input required type="email" autoComplete="email" className={`mt-1.5 ${inputCls}`} placeholder="you@email.com" onChange={set("email")} />
          </label>
          {cfg.fields.map((f) => (
            <label key={f.name} className="block sm:col-span-2">
              <span className="text-xs uppercase tracking-[0.12em] text-[#2D2D37]/50 font-medium">
                {f.label}
                {f.required ? " *" : ""}
              </span>
              <input required={!!f.required} className={`mt-1.5 ${inputCls}`} placeholder={f.placeholder} onChange={set(f.name)} />
            </label>
          ))}
          <label className="block sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.12em] text-[#2D2D37]/50 font-medium">Message</span>
            <textarea rows={4} className={`mt-1.5 ${inputCls}`} placeholder="Tell us a little more (optional)" onChange={set("message")} />
          </label>
        </div>
        <button
          type="submit"
          disabled={status === "sending"}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#254369] text-white rounded-xl px-6 py-3.5 font-medium hover:bg-[#2D2D37] transition-colors disabled:opacity-60 btn-shine"
        >
          <Send size={17} /> {status === "sending" ? "Sending…" : cfg.button}
        </button>
        <p className="mt-4 text-[11px] leading-relaxed text-[#2D2D37]/45 text-center">
          By submitting, you agree to be contacted by Wallstreet Realty by phone,
          email, or text about your inquiry. No spam, ever.
        </p>
      </form>
    </Reveal>
  );
};
